"use client";
import React, { useState } from "react";
import Link from "next/link";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS } from "@contentful/rich-text-types";
import {
  GA4DataLayer,
  GA4DataLayerFn,
} from "@packages/lib/utlils/helper-function";
import { GAConstants } from "@packages/shared-components/services/constants";

interface WuscacontentsectionProps {
  heading?: string;
  subheading?: string;
  description?: any;
  linkText?: string;
  linkUrl?: string;
  linkTarget?: string;
}

const Wuscacontentsection = ({
  heading,
  subheading,
  description,
  linkText,
  linkUrl,
  linkTarget,
}: WuscacontentsectionProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleReadMoreClick = () => {
    GA4DataLayer({
      event: GAConstants.track_event,
      event_name: "wuscalp_read_more_clicks",
      click_text: linkText,
    });
    GA4DataLayerFn({
      event: "wusca_events",
      eventName: "wuscalp_read_more_clicks",
      data_label: linkUrl,
    });
  };

  const renderDescription = () => {
    if (
      description &&
      typeof description === "object" &&
      description.nodeType
    ) {
      const options = {
        renderNode: {
          [BLOCKS.PARAGRAPH]: (_node: any, children: any) => (
            <p className="mb-[16px]">{children}</p>
          ),
        },
      };
      return documentToReactComponents(description, options);
    }
    if (description && typeof description === "string") {
      return description.split("\n\n").map((para: string, index: number) => (
        <p key={index} className="mb-[16px]">
          {para}
        </p>
      ));
    }
    return null;
  };

  return (
    <section className="bg-grey-50 flex flex-col items-center px-[16px] py-[40px] md:px-[20px] md:py-[64px] xl:px-0 gap-[32px]">
      <div className="max-w-[802px] w-full flex flex-col gap-[16px]">
        {heading && (
          <h1 className="font-farro font-bold text-heading2 md:text-heading-lg text-grey-900">
            {heading}
          </h1>
        )}

        {subheading && (
          <p className="font-semibold text-para-lg text-grey-900">
            {subheading}
          </p>
        )}

        <div className="text-para">
          <div
            className={`${!isExpanded ? "line-clamp-4 md:line-clamp-none" : ""}`}
          >
            {renderDescription()}
            {linkText && linkUrl && (
              <Link
                href={linkUrl}
                onClick={handleReadMoreClick}
                className="text-primary-400 font-semibold underline"
                target={linkTarget === "Open in new tab" ? "_blank" : "_self"}
                rel={
                  linkTarget === "Open in new tab"
                    ? "noopener noreferrer"
                    : undefined
                }
              >
                {linkText}
              </Link>
            )}
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex md:hidden items-center gap-[4px] text-primary-400 font-semibold small underline mt-[8px]"
          >
            {isExpanded ? "Read less" : "Read more"}
            <svg
              width="12"
              height="8"
              viewBox="0 0 12 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={`transition-transform ${isExpanded ? "rotate-180" : ""}`}
            >
              <path
                d="M1 1.5L6 6.5L11 1.5"
                stroke="#4664DC"
                strokeWidth="1.67"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
};

export default Wuscacontentsection;
